import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Alert,
  Paper,
  Switch,
  FormControlLabel,
  FormGroup,
  Button,
  Chip
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';

const SimpleDashboard: React.FC = () => {
  // Widget button visibility state
  const [widgetEnvironment, setWidgetEnvironment] = useState<string>('production');
  const [buttonVisibility, setButtonVisibility] = useState({
    welcomePopupCheckAvailability: false,
    welcomePopupTripStatus: false,
    footerCheckAvailability: false,
    footerTripStatus: false
  });
  const [voiceWidgetVisible, setVoiceWidgetVisible] = useState(false);
  const [savingButtons, setSavingButtons] = useState(false);
  const [buttonSuccess, setButtonSuccess] = useState<string | null>(null);
  const [buttonError, setButtonError] = useState<string | null>(null);

  useEffect(() => {
    loadWidgetConfig();
  }, []);

  const loadWidgetConfig = async () => {
    try {
      // Detect environment
      const hostname = window.location.hostname;
      const env = hostname.includes('localhost') ? 'staging' : 
                  hostname.includes('staging') ? 'staging' : 'production';
      setWidgetEnvironment(env);

      console.log('[Dashboard] Loading button visibility for environment:', env);
      
      // Use staging API for staging environment
      const baseUrl = env === 'staging' 
        ? 'https://us-central1-adrift-chatbot-staging.cloudfunctions.net'
        : 'https://us-central1-adriftchatbotv3.cloudfunctions.net';
      
      console.log('[Dashboard] API URL:', baseUrl);
      
      const response = await fetch(`${baseUrl}/getWidgetConfig?environment=${env}`);

      if (response.ok) {
        const data = await response.json();
        console.log('[Dashboard] Widget config response:', data);
        
        if (data.success && data.config?.buttons) {
          console.log('[Dashboard] Setting button visibility:', data.config.buttons);
          setButtonVisibility(data.config.buttons);
        } else {
          console.warn('[Dashboard] No button config found in response');
        }
        
        if (data.success && data.config?.voiceWidgetVisible !== undefined) {
          console.log('[Dashboard] Setting voice widget visibility:', data.config.voiceWidgetVisible);
          setVoiceWidgetVisible(data.config.voiceWidgetVisible);
        } else {
          console.log('[Dashboard] Voice widget visible not in config, using default:', false);
          setVoiceWidgetVisible(false);
        }
      } else {
        console.error('[Dashboard] Failed to load config, status:', response.status);
      }
    } catch (err) {
      console.error('[Dashboard] Error loading widget config:', err);
    }
  };

  const saveWidgetConfig = async () => {
    try {
      setSavingButtons(true);
      setButtonError(null);
      setButtonSuccess(null);

      console.log('[Dashboard] Saving widget config for environment:', widgetEnvironment);
      console.log('[Dashboard] Button visibility to save:', buttonVisibility);
      console.log('[Dashboard] Voice widget visible to save:', voiceWidgetVisible);
      
      // Use staging API for staging environment
      const baseUrl = widgetEnvironment === 'staging' 
        ? 'https://us-central1-adrift-chatbot-staging.cloudfunctions.net'
        : 'https://us-central1-adriftchatbotv3.cloudfunctions.net';
      
      console.log('[Dashboard] Save API URL:', baseUrl);
      
      // Get current popup config first (we need to send it along with buttons)
      const getResponse = await fetch(`${baseUrl}/getWidgetConfig?environment=${widgetEnvironment}`);
      let popupConfig = { enabled: false, message: '', displayDuration: 5000 };
      
      if (getResponse.ok) {
        const getData = await getResponse.json();
        if (getData.success && getData.config?.popup) {
          popupConfig = getData.config.popup;
        }
      }

      // Prepare the request body
      const requestBody = {
        environment: widgetEnvironment,
        popup: popupConfig,
        buttons: buttonVisibility,
        voiceWidgetVisible: voiceWidgetVisible
      };
      
      console.log('[Dashboard] Request body:', JSON.stringify(requestBody, null, 2));

      // Now update with button visibility and voice widget visibility
      const response = await fetch(`${baseUrl}/updateWidgetConfig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'geoff@emertech.nz'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('[Dashboard] Save response status:', response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log('[Dashboard] ✅ Widget config saved successfully:', responseData);
        setButtonSuccess('Widget settings saved successfully!');
        setTimeout(() => setButtonSuccess(null), 5000);
        // Reload config to ensure UI is in sync
        setTimeout(() => loadWidgetConfig(), 500);
      } else {
        const errorData = await response.json();
        console.error('[Dashboard] ❌ Save failed:', errorData);
        setButtonError(errorData.message || 'Failed to save widget settings');
      }
    } catch (err: any) {
      console.error('[Dashboard] ❌ Error saving widget config:', err);
      setButtonError(err.message || 'Failed to save button visibility settings');
    } finally {
      setSavingButtons(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Adrift Admin Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="success" sx={{ mb: 3 }}>
            ✅ Successfully connected to live Firebase Functions with service account access!
          </Alert>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎯 Data Access
              </Typography>
              <Typography>
                • Ratings data: ✅ Live via service account<br/>
                • RAG/Chunks data: ✅ Live via service account<br/>
                • Analytics: ✅ Live via service account
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Available Pages
              </Typography>
              <Typography>
                • Ratings: ✅ Working with live data<br/>
                • AI Data: ✅ Working with live data<br/>
                • Others: 🔄 Being updated to service account APIs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Widget Button Visibility Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Chat Widget Button Visibility
              </Typography>
              <Chip 
                label={widgetEnvironment}
                size="small" 
                color={widgetEnvironment === 'production' ? 'error' : 'warning'}
                sx={{ ml: 2, fontWeight: 600 }}
              />
            </Box>

            {buttonSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setButtonSuccess(null)}>
                {buttonSuccess}
              </Alert>
            )}

            {buttonError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setButtonError(null)}>
                {buttonError}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Welcome Popup Section */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Welcome Popup Buttons
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={buttonVisibility.welcomePopupCheckAvailability}
                          onChange={(e) => setButtonVisibility({
                            ...buttonVisibility,
                            welcomePopupCheckAvailability: e.target.checked
                          })}
                          disabled={savingButtons}
                        />
                      }
                      label="Check Availability Button"
                    />
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={buttonVisibility.welcomePopupTripStatus}
                          onChange={(e) => setButtonVisibility({
                            ...buttonVisibility,
                            welcomePopupTripStatus: e.target.checked
                          })}
                          disabled={savingButtons}
                        />
                      }
                      label="Trip Status Button"
                    />
                  </FormGroup>
                </Paper>
              </Grid>
              
              {/* Footer Section */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Chat Footer Buttons
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={buttonVisibility.footerCheckAvailability}
                          onChange={(e) => setButtonVisibility({
                            ...buttonVisibility,
                            footerCheckAvailability: e.target.checked
                          })}
                          disabled={savingButtons}
                        />
                      }
                      label="Check Availability Button"
                    />
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={buttonVisibility.footerTripStatus}
                          onChange={(e) => setButtonVisibility({
                            ...buttonVisibility,
                            footerTripStatus: e.target.checked
                          })}
                          disabled={savingButtons}
                        />
                      }
                      label="Trip Status Button"
                    />
                  </FormGroup>
                </Paper>
              </Grid>
            </Grid>
            
            <Box mt={3} display="flex" gap={2}>
              <Button 
                variant="contained" 
                onClick={saveWidgetConfig}
                disabled={savingButtons}
              >
                {savingButtons ? 'Saving...' : 'Save Button Settings'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={loadWidgetConfig}
                disabled={savingButtons}
              >
                Refresh
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Voice Widget Visibility Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Voice Widget Visibility
              </Typography>
              <Chip 
                label={widgetEnvironment}
                size="small" 
                color={widgetEnvironment === 'production' ? 'error' : 'warning'}
                sx={{ ml: 2, fontWeight: 600 }}
              />
            </Box>

            {buttonSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setButtonSuccess(null)}>
                {buttonSuccess}
              </Alert>
            )}

            {buttonError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setButtonError(null)}>
                {buttonError}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={voiceWidgetVisible}
                    onChange={(e) => setVoiceWidgetVisible(e.target.checked)}
                    disabled={savingButtons}
                  />
                }
                label="Voice Widget Button"
              />
            </Paper>
            
            <Box mt={3} display="flex" gap={2}>
              <Button 
                variant="contained" 
                onClick={saveWidgetConfig}
                disabled={savingButtons}
              >
                {savingButtons ? 'Saving...' : 'Save Voice Settings'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={loadWidgetConfig}
                disabled={savingButtons}
              >
                Refresh
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SimpleDashboard;



